import React, {useEffect, useState} from "react";
import {Accordion, ButtonGroup, Col, Container, Dropdown, DropdownButton, Form, InputGroup, Row} from "react-bootstrap";
import {
    CORPORATION,
    CORP_TYPE,
    JobDetailType,
    getJobDescriptionListHelper,
    generateJobDescriptionObject,
    generateJobChallengeObject,
    JobDescriptionDetailsType,
    JobChallengeDetailsType,
} from "./jobDetailsManagementHelper";
import "./jobDetailsManagement.css";
import {JobDescription} from "./Description/JobDescription";
import {JobChallengeInfo} from "./ChallengeInfo/JobChallengeInfo";
import {CopyModal} from "./Modals/CopyModal";
import {FILTER_DEFAULT, FILTER_TYPE, JobFilters} from "./Filters/Filters";
import {JobList} from "./JobList/JobList";

export const JobDetailsManagement: React.FC<{}> = () => {
    const [isLoadingJobList, setLoadingJobList] = useState<boolean>(false);
    const [filters, setFilters] = useState<FILTER_TYPE>(FILTER_DEFAULT);
    const [jobCorpType, setJobCorpType] = useState<CORP_TYPE>();
    const [jobList, setJobList] = useState<JobDetailType[]>();
    const [selectedJob, setSelectedJob] = useState<JobDetailType | undefined>();
    const [search, setSearch] = useState<string>("");

    const [jobDescription, setJobDescription] = useState<JobDescriptionDetailsType[][]>();
    const [jobChallenge, setJobChallenge] = useState<JobChallengeDetailsType[]>();

    // clone feature
    const [isCopyModalOpen, setCopyModalOpen] = useState<boolean>(false);
    const [copyModalAction, setCopyModalAction] = useState<"clone-to" | "clone-from">("clone-to");
    const [copyTo, setCopyTo] = useState<JobDetailType | undefined>(undefined);

    const loadJobList = async (corpType: CORP_TYPE) => {
        setLoadingJobList(true);
        const response = await getJobDescriptionListHelper(corpType);
        if (isCopyModalOpen && copyTo && copyModalAction === "clone-to") {
            const updatedCloneJob = response.find((job: any) => job.id === copyTo.id);
            if (updatedCloneJob) setSelectedJob(updatedCloneJob);
        } else if (selectedJob) {
            const updatedSelectedJob = response.find((job: any) => job.id === selectedJob.id);
            if (updatedSelectedJob) setSelectedJob(updatedSelectedJob);
        }
        setJobList(response);
        setLoadingJobList(false);
    };

    const reset = () => {
        setSelectedJob(undefined);
        setFilters(FILTER_DEFAULT);
        setSearch("");
    };

    useEffect(() => {
        if (jobCorpType) {
            loadJobList(jobCorpType);
            reset();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobCorpType]);

    useEffect(() => {
        if (selectedJob) {
            setJobDescription(generateJobDescriptionObject(selectedJob.description));
            if (jobCorpType === CORP_TYPE.APPRENTICESHIP)
                setJobChallenge(generateJobChallengeObject(selectedJob.challengeInfo));
        }
    }, [selectedJob, jobCorpType]);

    console.log({selectedJob});

    return (
        <div className="">
            <Container>
                {/* Job Type Dropdown and Filter Sections */}
                <Row>
                    <Col sm={4}>
                        <div className="action-container">
                            <p>
                                <strong>Please select the job type:</strong>
                            </p>
                            <Dropdown style={{pointerEvents: isLoadingJobList ? "none" : "auto"}}>
                                <Dropdown.Toggle id="dropdown-autoclose-inside" className="dropdown-box">
                                    {jobCorpType ? `${CORPORATION[jobCorpType].label}` : "Select job type"}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item
                                        onClick={() => {
                                            setJobCorpType(CORP_TYPE.APPRENTICESHIP);
                                            setSelectedJob(undefined);
                                        }}
                                    >
                                        {CORPORATION[CORP_TYPE.APPRENTICESHIP].label}
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        onClick={() => {
                                            setJobCorpType(CORP_TYPE.STAFF_AUG);
                                            setSelectedJob(undefined);
                                        }}
                                    >
                                        {CORPORATION[CORP_TYPE.STAFF_AUG].label}
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </Col>
                    <Col sm={8}>
                        {jobCorpType && <JobFilters filters={filters} setFilters={setFilters} resetAll={reset} />}
                    </Col>
                </Row>
                <hr />
                {/* Job List and Detail Edit Sections */}
                <Row>
                    <Col sm={4}>
                        {jobCorpType && (
                            <div className="action-container">
                                {isLoadingJobList ? (
                                    <p>
                                        <strong>Loading Jobs...</strong>
                                    </p>
                                ) : (
                                    <>
                                        <div>
                                            <strong>
                                                Jobs for {CORPORATION[jobCorpType].label}:
                                                <InputGroup className="mb-3">
                                                    <Form.Control
                                                        placeholder="Search by Job Name or Title"
                                                        aria-label="Search"
                                                        aria-describedby="basic-addon1"
                                                        value={search}
                                                        onChange={(e) => setSearch(e.target.value)}
                                                    />
                                                </InputGroup>
                                            </strong>
                                        </div>
                                        {/* search bar, filter options */}
                                        <JobList
                                            jobList={jobList || []}
                                            selectedJob={selectedJob}
                                            onClick={(j: JobDetailType) => {
                                                setSelectedJob(j);
                                            }}
                                            filters={filters}
                                            search={search}
                                        />
                                    </>
                                )}
                                <br />
                            </div>
                        )}
                    </Col>
                    <Col sm={8}>
                        {!isLoadingJobList && jobCorpType && selectedJob && jobDescription !== undefined ? (
                            <>
                                <Row>
                                    <Col sm={9}>
                                        <p>
                                            <a
                                                href={`${
                                                    jobCorpType === CORP_TYPE.STAFF_AUG
                                                        ? `https://smoothstack.lightning.force.com/lightning/r/Opportunity/${selectedJob.salesforceId}/view`
                                                        : `https://smoothstack.lightning.force.com/lightning/r/Job__c/${selectedJob.salesforceId}/view`
                                                }`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <strong>{selectedJob.label}</strong>
                                            </a>
                                        </p>
                                    </Col>
                                    <Col sm={3}>
                                        <DropdownButton
                                            title="Actions"
                                            as={ButtonGroup}
                                            size="sm"
                                            key="job-actions"
                                            variant="secondary"
                                            id="dropdown-button-job-actions"
                                            style={{float: "right"}}
                                        >
                                            <Dropdown.Item
                                                onClick={() => {
                                                    setCopyModalAction("clone-to");
                                                    setCopyModalOpen(true);
                                                }}
                                                disabled={
                                                    !(jobCorpType && selectedJob && jobList && jobList.length > 1)
                                                }
                                            >
                                                Clone
                                            </Dropdown.Item>
                                            <Dropdown.Item
                                                onClick={() => {
                                                    setCopyModalAction("clone-from");
                                                    setCopyModalOpen(true);
                                                }}
                                                disabled={
                                                    !(jobCorpType && selectedJob && jobList && jobList.length > 1)
                                                }
                                            >
                                                Clone From
                                            </Dropdown.Item>
                                        </DropdownButton>
                                    </Col>
                                </Row>
                                <Accordion>
                                    <Accordion.Item eventKey="0">
                                        <Accordion.Header>
                                            <strong>Job Description Details</strong>
                                        </Accordion.Header>
                                        <Accordion.Body className="float-div">
                                            {!isLoadingJobList && (
                                                <JobDescription
                                                    jobCorpType={jobCorpType}
                                                    selectedJob={selectedJob}
                                                    jobDescription={jobDescription}
                                                    setJobDescription={setJobDescription}
                                                    reload={() => loadJobList(jobCorpType)}
                                                />
                                            )}
                                        </Accordion.Body>
                                    </Accordion.Item>
                                    {jobCorpType === "APPRENTICESHIP" && (
                                        <Accordion.Item eventKey="1">
                                            <Accordion.Header>
                                                <strong>Challenge Information Details</strong>
                                            </Accordion.Header>
                                            <Accordion.Body className="float-div">
                                                <JobChallengeInfo
                                                    jobCorpType={jobCorpType}
                                                    selectedJob={selectedJob}
                                                    jobChallenge={jobChallenge || ([] as JobChallengeDetailsType[])}
                                                    setJobChallenge={setJobChallenge}
                                                    reload={() => loadJobList(jobCorpType)}
                                                />
                                            </Accordion.Body>
                                        </Accordion.Item>
                                    )}
                                </Accordion>
                            </>
                        ) : (
                            <>
                                {isLoadingJobList && <p>Loading...</p>}
                                {jobCorpType && !isLoadingJobList && (
                                    <p>Details will be displayed once job is selected.</p>
                                )}
                            </>
                        )}
                    </Col>
                </Row>
                {jobCorpType && selectedJob && (
                    <>
                        {" "}
                        <CopyModal
                            show={isCopyModalOpen}
                            onHide={() => {
                                setCopyModalOpen(false);
                            }}
                            action={copyModalAction}
                            corpType={jobCorpType}
                            jobList={jobList || []}
                            selectedJob={selectedJob}
                            setSelectedJob={setSelectedJob}
                            copyTo={copyTo}
                            setCopyTo={setCopyTo}
                            reload={() => loadJobList(jobCorpType)}
                        />
                    </>
                )}
            </Container>
        </div>
    );
};
